import Actions from 'flow/actions';
import {
    ActiveCallDirections,
    convertNumberToE164,
    toNationalNumberFormat,
} from 'flow/utils/call-utils';
import { fetchFeatureFlagsForUser } from 'flow/actions/app-actions';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import {
    getActionFromIncomingEvent,
    OUTGOING_EVENTS,
    INCOMING_EVENTS,
    INCOMING_POSTMESSAGE_API_FORMAT,
    OUTGOING_POSTMESSAGE_API_FORMAT,
} from 'flow/types/integration-event-types';
import { addCtiLogEntry } from 'flow/utils/browser-utils';
import HomeClient from 'flow/api-clients/home-client';
import { isEmptyNullOrUndefined, tryParseJsonObject } from 'flow/utils';
import {
    getFocusedActiveCall,
    getFocusedSession,
    getLocalCallIds,
    getOwnActiveCalls,
} from 'flow/selectors/call-selectors';
import { hasCtiSettings, getCtiSetting } from 'flow/utils/cti-settings-utils';
import {
    getIntegrationContactMetaDataByNumber,
    allowCtiCommunication,
} from 'flow/selectors/cti-integration-selectors';
import { selectProfileRecordCalls } from 'flow/selectors/profile-selectors';
import { fetchCallRecord } from 'flow/utils/cti-call-log-utils';
import { getBrand } from 'flow/brands/brands';
import {
    isMainRegistration,
    setMainRegistration,
} from 'flow/utils/instance-count-utils';
import {
    getOwnCountryCode,
    getOwnExtensionKey,
} from 'flow/selectors/selectors';
import { selectQueueDescription } from 'flow/selectors/queue-selectors';
import { ctiTraceLog } from 'flow/utils/cti-call-log-utils';
import appConfig from 'app-config';

const INIT_RETRIES = 100;
export const CTI_INTEGRATION_CLIENT_TYPE_HEADER = 'cti-integration';

const send = (type, data) => (_dispatch, getState) => {
    const { integration } = getState();

    if (!allowCtiCommunication(getState())) {
        addCtiLogEntry(
            `Won't send ${type} message since user doesn't have CTI integration feature.`,
        );
        return;
    }

    const targetOrigin = integration.get('targetOrigin');

    const message = { type, ...data };
    addCtiLogEntry('Send message to host system', message, targetOrigin);

    if (targetOrigin) {
        parent.postMessage(message, targetOrigin);
    } else {
        addCtiLogEntry(
            `Won't send ${type} message since no target origin was found.`,
        );
    }
};

const clearCallLogSent = () => {
    return { type: Actions.CTI_CLEAR_CALL_LOG_SENT };
};

const setCallLogSent = () => {
    return { type: Actions.CTI_CALL_LOG_SENT };
};

export const sendInitializedNotification =
    (loggedIn) => (_dispatch, getState) => {
        const message = {
            init: 'ialized',
            loggedIn,
        };
        if (isEmptyNullOrUndefined(loggedIn)) {
            throw new Error('Invalid loggedIn status.');
        }

        let resendCounter = 0;
        const sendInitializedNotificationUntilHostRegisters = () => {
            const { integration } = getState();
            const targetOrigin = integration.get('targetOrigin');

            if (resendCounter > INIT_RETRIES) {
                addCtiLogEntry(
                    `No origin registration after ${resendCounter} tries. Stoping.`,
                );
                return;
            }

            if (!targetOrigin) {
                addCtiLogEntry(
                    `Sending an init message to host system. Expecting origin registration. Sent message ${++resendCounter} time(s)`,
                );
                if (hasCtiSettings(getState())) {
                    message.ctiSettings = getCtiSetting(getState());
                }
                parent.postMessage(message, '*');
                setTimeout(
                    sendInitializedNotificationUntilHostRegisters,
                    resendCounter * 250,
                );
            }
        };

        sendInitializedNotificationUntilHostRegisters();
    };

export const userLoggedIn = () => (dispatch, getState) => {
    const { customer } = getState();
    const extensionKey = getOwnExtensionKey(getState());
    const customerKey = customer.getIn([extensionKey, 'key']);
    const message = {
        extensionKey,
        customerKey,
    };
    dispatch(send(OUTGOING_EVENTS.USER_LOGGED_IN, message));
    dispatch({ type: Actions.CTI_USER_LOGGED_IN });
};

export const userLoggedOut = () => (dispatch) => {
    dispatch(send(OUTGOING_EVENTS.USER_LOGGED_OUT));
    dispatch({ type: Actions.CTI_USER_LOGGED_OUT });
};

export const tooManyInstances = (tooManyInstances) => {
    return {
        type: Actions.CTI_SET_TOO_MANY_INSTANCES,
        data: { tooManyInstances },
    };
};

const getFormattedDirection = (call) =>
    call.direction === ActiveCallDirections.INCOMING.value
        ? INCOMING_POSTMESSAGE_API_FORMAT
        : OUTGOING_POSTMESSAGE_API_FORMAT;
const getMyNumber = (call) =>
    call.direction === ActiveCallDirections.INCOMING.value
        ? call.destination
        : call.source;
const getOtherNumber = (call) =>
    call.direction === ActiveCallDirections.INCOMING.value
        ? call.source
        : call.destination;

export const sendCallLog =
    (callInfo, focusedActiveCall) => (dispatch, getState) => {
        addCtiLogEntry('Call info used for call log', callInfo);
        if (focusedActiveCall) {
            addCtiLogEntry('Focused active call', focusedActiveCall.toJS());
        } else {
            addCtiLogEntry('Focused active call was undefined');
        }
        const unansweredQueue =
            focusedActiveCall.get('state') === 'ringing' &&
            focusedActiveCall.get('queue');
        if (unansweredQueue) {
            // We only log calls that are picked up by an agent in the queue. IE when State is up and Queue is not undefined.
            // This is to avoid logging many call logs when the queue rotates through- or calls all members of the queue.
            addCtiLogEntry(
                'Attempted to send call log when call was picked up outside queue.',
            );
            return;
        }

        if (!callInfo) {
            addCtiLogEntry(
                'Attempted to send call log when no active call was present. This should not happen!',
            );
            return;
        }

        const metadata = getIntegrationContactMetaDataByNumber(
            getState(),
            callInfo.remoteNumber,
        );
        const ctiSettings = getCtiSetting(getState());
        const data = {
            ...(ctiSettings &&
                ctiSettings.logCallsAutomatically && { ctiSettings }),
            myNumber: callInfo.localNumber,
            call: {
                ...(callInfo.durationMilliSeconds && {
                    durationMilliSeconds: callInfo.durationMilliSeconds,
                }),
                ...(callInfo.durationSeconds && {
                    durationSeconds: callInfo.durationSeconds,
                }),
                ...(callInfo.recordingUrl && {
                    recordingUrl: callInfo.recordingUrl,
                }),
                ...(callInfo.recordingPageUrl && {
                    recordingPageUrl: callInfo.recordingPageUrl,
                }),
                ...(callInfo.answered && { answered: callInfo.answered }),
                ...(callInfo.disconnected && {
                    disconnected: callInfo.disconnected,
                }),
                numberE164: callInfo.remoteNumber,
                number: toNationalNumberFormat(callInfo.remoteNumber),
                direction: callInfo.direction,
            },
            ...(metadata && { metadata }),
        };

        // Only send call log if the instance is the one where the call was made/accepted from
        if (isMainRegistration()) {
            dispatch(send(OUTGOING_EVENTS.CALL_LOG, data));
            dispatch(setCallLogSent());
        } else {
            addCtiLogEntry(
                'isMainRegistration is false, the CALL_LOG event will not be sent',
            );
        }
    };

const UNKNOWN_CALLER_ID = '<unknown>';

const unlessUnknown = (value) =>
    value === UNKNOWN_CALLER_ID ? undefined : value;

// The caller of an incoming call is in 'source', except for a queue call,
// where Top can send it in 'originalCallerId' instead. A queue that could not
// identify the caller sends '<unknown>' or nothing at all, and then we have no
// number to report. Both fields can hold a national number, so convert.
//
// The sentinel is filtered per field before falling back: 'originalCallerId'
// is stored raw ('top-actions.js', 'updateActiveCall'), so collapsing first
// would let an unidentified queue caller hide a real number in 'source'.
const getIncomingCallerNumber = (activeCall, state) => {
    const fromTop =
        unlessUnknown(activeCall.get('originalCallerId')) ||
        unlessUnknown(activeCall.get('source'));

    if (!fromTop) {
        return undefined;
    }

    return convertNumberToE164(fromTop, getOwnCountryCode(state));
};

// Only the 'QueueCallInfo' event names the queue a call came from, and not
// every queue sends a name with it. The call still carries the queue it came
// from, so ask the queue itself for its name when the event had none.
const getQueueName = (activeCall, state) => {
    const queueName = activeCall.get('queueName');

    if (queueName) {
        return queueName;
    }

    const queueKey = activeCall.get('queue');
    return queueKey ? selectQueueDescription(state, queueKey) : undefined;
};

export const ctiCallInfoFromActiveCall = (activeCall, state) => {
    return {
        queueName: getQueueName(activeCall, state),
        startTime: activeCall.get('startTime'),
        direction:
            activeCall.get('direction') === ActiveCallDirections.INCOMING.value
                ? INCOMING_POSTMESSAGE_API_FORMAT
                : OUTGOING_POSTMESSAGE_API_FORMAT,
        localNumber:
            activeCall.get('direction') === ActiveCallDirections.INCOMING.value
                ? activeCall.get('destination')
                : activeCall.get('source'),
        remoteNumber:
            activeCall.get('direction') === ActiveCallDirections.INCOMING.value
                ? getIncomingCallerNumber(activeCall, state)
                : activeCall.get('destination'),
    };
};

// Shared by handleNewCall and redecorateCall, so a lookup made again sends
// the same fields as the first one.
const decorateActiveCall = (activeCall, callInfo) => {
    const bagVariables = activeCall.get('bagVariables');

    return getDecoratedCall(
        callInfo.remoteNumber,
        callInfo.direction,
        bagVariables && bagVariables.toJS(),
        callInfo.queueName,
        callInfo.startTime,
    );
};

// NewChannel already delivers the variables set before the call reached this
// extension; they come again as SetBagVariable. Tell those repeats apart.
export const isBagVariableChanged = (
    state,
    extensionKey,
    callKey,
    variableName,
    variableValue,
) => {
    const previousValue = state.extensions
        .getIn(['activeLineData', extensionKey])
        ?.find((call) => call.get('key') === callKey)
        ?.getIn(['bagVariables', variableName]);
    return (
        JSON.stringify(previousValue) !==
        JSON.stringify(tryParseJsonObject(variableValue))
    );
};

// Bag variables can arrive after the call has started. Look the contact up
// again with the full set, so a lookup made without them (by phone number)
// is replaced without waiting for the next call event.
export const redecorateCall = (activeCallKey) => (dispatch, getState) => {
    const focusedActiveCall = getFocusedActiveCall(getState());
    if (!focusedActiveCall || focusedActiveCall.get('key') !== activeCallKey) {
        return;
    }
    const callInfo = ctiCallInfoFromActiveCall(focusedActiveCall, getState());

    dispatch(decorateActiveCall(focusedActiveCall, callInfo));
};

export const handleNewCall = (activeCallKey) => (dispatch, getState) => {
    const focusedActiveCall = getFocusedActiveCall(getState());
    const isActiveCallKeyFocusedActiveCall = focusedActiveCall
        ? focusedActiveCall.get('key') === activeCallKey
        : false;
    if (!isActiveCallKeyFocusedActiveCall) {
        // The event was disptached for a call other than the focused active call. We don't care about these.
        return;
    }
    const callInfo = ctiCallInfoFromActiveCall(focusedActiveCall, getState());
    const metadata = getIntegrationContactMetaDataByNumber(
        getState(),
        callInfo.remoteNumber,
    );

    dispatch(decorateActiveCall(focusedActiveCall, callInfo));

    switch (callInfo.direction) {
        case INCOMING_POSTMESSAGE_API_FORMAT: {
            dispatch(
                send(OUTGOING_EVENTS.INCOMING_CALL, {
                    number: callInfo.remoteNumber,
                    numberE164: callInfo.remoteNumber,
                    ...(metadata && { metadata }),
                }),
            );
            break;
        }
        case OUTGOING_POSTMESSAGE_API_FORMAT: {
            dispatch(
                send(OUTGOING_EVENTS.OUTGOING_CALL_STARTED, {
                    number: callInfo.remoteNumber,
                    numberE164: callInfo.remoteNumber,
                    ...(metadata && { metadata }),
                }),
            );
            break;
        }
    }
};

export const callHungup =
    (activeCallKey, locutusSessionId) => async (dispatch, getState) => {
        try {
            const focusedActiveCall = getFocusedActiveCall(getState());
            const numberOfActiveCalls = getOwnActiveCalls(getState()).size;
            ctiTraceLog(
                getState(),
                `numberOfActiveCalls: ${numberOfActiveCalls}`,
                locutusSessionId,
            );
            const isActiveCallKeyFocusedActiveCall = focusedActiveCall
                ? focusedActiveCall.get('key') === activeCallKey
                : false;
            if (!isActiveCallKeyFocusedActiveCall || numberOfActiveCalls > 1) {
                ctiTraceLog(
                    getState(),
                    `The event was dispatched for a call other than the focused active call isActiveCallKeyFocusedActiveCall: ${isActiveCallKeyFocusedActiveCall} numberOfActiveCalls: ${numberOfActiveCalls}`,
                    locutusSessionId,
                );
                // The event was dispatched for a call other than the focused active call. We don't care about these.
                // If there is more than one active call left, then this client was denied the call, either from an
                // enduser hangup, or from a nother client picking it up (making this active call hang up). Either way,
                // once the session actually ends, there will be another hangup, so chill.
                return;
            }
            dispatch(send(OUTGOING_EVENTS.CALL_ENDED));

            // If host system is Zendesk and customer has Zendesk Call Logging installed,
            // don't send CALL_LOG to host system.
            // This is to be able to release Zendesk 2.0 without breaking Zendesk 1.0,
            // https://telavox.atlassian.net/browse/INTE-4104.
            if (
                getState().integration.getIn([
                    'integrationConfig',
                    'hostDescription',
                ]) === 'Zendesk' &&
                getState().app.getIn(['extension', 'hasZendeskCallLoggingApp'])
            ) {
                addCtiLogEntry(
                    'User has installed Zendesk Call Logging, CALL_LOG message will not be sent to host system',
                );
                return;
            }

            const callInfo = ctiCallInfoFromActiveCall(
                focusedActiveCall,
                getState(),
            );
            // Locutus ID will not be available everywhere until all SIP servers / TOP servers are upgraded
            // with the following patch: https://github.com/Telavox/proxy/pull/1098. When it's rolled out
            // everywhere we can remove the if-statement below.
            if (locutusSessionId) {
                const callRecordDto =
                    await fetchCallRecord(locutusSessionId)(dispatch);
                const { disconnected, answered } = callRecordDto;
                ctiTraceLog(
                    getState(),
                    `disconnected ${disconnected} answered ${answered}`,
                    locutusSessionId,
                );
                if (disconnected && answered) {
                    const start = new Date(answered).getTime();
                    const finish = new Date(disconnected).getTime();

                    callInfo.durationMilliSeconds = finish - start;
                    callInfo.durationSeconds = Math.round(
                        callInfo.durationMilliSeconds / 1000,
                    );
                    callInfo.answered = start;
                    callInfo.disconnected = finish;
                }

                if (selectProfileRecordCalls(getState())) {
                    const host = HomeClient.apiHost || appConfig.apiUrl;
                    callInfo.recordingUrl = `${host}/api/internal/calls/call-${locutusSessionId}/recording`;
                }
                const brandAppUrl = getBrand().brandAppUrl;
                if (brandAppUrl) {
                    callInfo.recordingPageUrl = `${
                        getBrand().brandAppUrl
                    }/callrecording/call-${locutusSessionId}`;
                }

                ctiTraceLog(
                    getState(),
                    `recordingUrl ${callInfo.recordingUrl} recordingPageUrl ${callInfo.recordingPageUrl}`,
                    locutusSessionId,
                );
            }
            dispatch(sendCallLog(callInfo, focusedActiveCall));
            dispatch(clearCallLogSent());
        } catch (error) {
            ctiTraceLog(
                getState(),
                `callHungup ${JSON.stringify(error)}`,
                locutusSessionId,
            );
        }
    };

export const callAnswered = (activeCallKey) => (dispatch, getState) => {
    const ownActiveCalls = getOwnActiveCalls(getState());
    const indexOfActiveCall = ownActiveCalls.findIndex(
        (activeCall) => activeCall.get('key') === activeCallKey,
    );
    if (indexOfActiveCall === -1) {
        // The event was dispatched for a call other than our calls. We don't care about these.
        // A call can only be answered by one device, so this is enough to decide the logged-in
        // user answered a call "somwhere".
        return;
    }

    const localCallIds = getLocalCallIds(getState());
    const activeCallId = ownActiveCalls.getIn([indexOfActiveCall, 'callId']);
    if (localCallIds.includes(activeCallId)) {
        setMainRegistration();
    }

    const ctiSettings = getCtiSetting(getState());
    const focusedActiveCall = getFocusedActiveCall(getState());
    const locutusSessionId = focusedActiveCall.get('locutusSessionId');
    const {
        localNumber,
        remoteNumber: number,
        direction,
        queueName,
        startTime,
    } = ctiCallInfoFromActiveCall(focusedActiveCall, getState());

    const data = {
        openOnAnswer: ctiSettings.openOnAnswer?.value,
        myNumber: localNumber,
        callInfo: {
            direction: capitalizeFirstLetter(direction),
            number,
            locutusSessionId,
            queueName,
            startTime,
        },
    };

    dispatch(send(OUTGOING_EVENTS.CALL_ANSWERED, data));
};

export const getContactDecorations = (calls) => (dispatch) => {
    if (!(calls && calls.records)) {
        return;
    }

    const numbersSet = new Set(
        calls.records.map((call) => {
            return call.number.number;
        }),
    );

    dispatch(
        send(OUTGOING_EVENTS.GET_CONTACT_DECORATIONS, {
            numbers: Array.from(numbersSet),
        }),
    );
};

export const getDecoratedCall =
    (numberE164, direction, bagVariables, queueName, startTime) =>
    (dispatch) => {
        dispatch(
            send(OUTGOING_EVENTS.GET_CONTACT_DECORATIONS, {
                numbers: [numberE164],
                direction,
                bagVariables,
                queueName,
                startTime,
            }),
        );
    };

export const sendProxyResponse = (response, proxyRequestId) => (dispatch) => {
    dispatch(
        send(OUTGOING_EVENTS.PROXY_REQUEST_RESPONSE, {
            response,
            proxyRequestId,
        }),
    );
};

const capitalizeFirstLetter = (string) => {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : null;
};

export const handleCtiClick =
    (routeInfo, myNumber, callInfo, metaData) => (dispatch, getState) => {
        if (!routeInfo) {
            return;
        }
        const { app, integration } = getState();
        const activeCall = integration.get('activeCall');

        const metadata =
            callInfo && callInfo.number
                ? getIntegrationContactMetaDataByNumber(
                      getState(),
                      callInfo.number,
                  )
                : {};
        let locutusSessionId;
        const focusedActiveCall = getFocusedActiveCall(getState());
        if (focusedActiveCall) {
            //event incoming outgoing call
            locutusSessionId = focusedActiveCall.get('locutusSessionId');
        } else if (callInfo && callInfo.callRecordKey) {
            //event from history
            locutusSessionId = callInfo.callRecordKey.replace('call-', '');
        }
        const ctiSettings = getCtiSetting(getState());

        if (callInfo === undefined) {
            const data = { routeInfo };
            data.ctiSettings = ctiSettings;
            if (activeCall) {
                const number = getOtherNumber(activeCall);
                const direction = getFormattedDirection(activeCall);
                const myNumber = getMyNumber(activeCall);
                data.callInfo = {
                    direction: capitalizeFirstLetter(direction),
                    number,
                };
                data.myNumber = myNumber;
            }
            if (metaData) {
                data.metadata = metaData;
            }
            data.hasCallLoggingApp = app.getIn([
                'extension',
                'hasZendeskCallLoggingApp',
            ]);
            dispatch(send(OUTGOING_EVENTS.PARENT_ROUTE_REQUEST, data));
        } else {
            const data = {
                ...{
                    callInfo: {
                        ...callInfo,
                        direction: capitalizeFirstLetter(callInfo.direction),
                        locutusSessionId,
                    },
                },
                routeInfo,
                myNumber,
                ...(metadata && { metadata }),
                ctiSettings,
                hasCallLoggingApp: app.getIn([
                    'extension',
                    'hasZendeskCallLoggingApp',
                ]),
            };
            dispatch(send(OUTGOING_EVENTS.PARENT_ROUTE_REQUEST, data));
        }
    };

export const setSelectedCtiContact = (contact) => ({
    type: Actions.CTI_SET_SELECTED_CONTACT,
    contact,
});

const proxyRequestCallback = (dispatch, proxyRequestId) => {
    return {
        onResult: (result) => {
            const response = {
                ok: true,
                status: result.statusCode,
                proxyRequestId,
                body: JSON.parse(result.data),
            };
            dispatch(sendProxyResponse(response, proxyRequestId));
        },
        onClientError: (error) => {
            const response = {
                ok: false,
                status: error.status,
                statusText: error.statusText,
                response: error.response,
                responseText: error.responseText,
                proxyRequestId,
            };
            dispatch(sendProxyResponse(response, proxyRequestId));
        },
        onError: (error) => {
            const response = {
                ok: false,
                status: error.status,
                statusText: error.statusText,
                response: error.response,
                responseText: error.responseText,
                proxyRequestId,
            };
            dispatch(sendProxyResponse(response, proxyRequestId));
        },
    };
};

const performProxyRequest = (callback, urlEnding, options) => {
    const conf = {
        path: urlEnding,
        method: 'GET',
        ...(options !== undefined && options),
    };
    HomeClient.exec(callback, conf);
};

const handleEventFromHostSystem = (dispatch, getState) => {
    return (event) => {
        addCtiLogEntry('Got message from host system.', event.data);
        const typeAsString = event.data.type;
        const actionType = getActionFromIncomingEvent(typeAsString);

        if (typeAsString === INCOMING_EVENTS.PROXY_REQUEST) {
            const { url, options, proxyRequestId } = event.data;
            const session = getFocusedSession(getState());
            const logId = session && session.data.logId;

            const optionsWithLogId = options || {};
            optionsWithLogId.params = {
                ...optionsWithLogId.params,
                logId,
            };

            performProxyRequest(
                proxyRequestCallback(dispatch, proxyRequestId),
                url,
                optionsWithLogId,
            );
        } else if (actionType) {
            addCtiLogEntry(
                'Message matches reducer action. Dispatching',
                actionType,
            );
            const action = {
                data: event.data,
                targetOrigin: event.origin,
                type: actionType,
            };
            dispatch(action);
        } else if (typeAsString === INCOMING_EVENTS.DIAL_NUMBER) {
            addCtiLogEntry(
                'Message matches dial. Dispatching show call dialog.',
            );
            dispatch(showCallDialog(event.data.phoneNumber));
        } else {
            addCtiLogEntry(
                'Message did not match any usecase. No action taken.',
            );
        }
    };
};

export const setHomeClientHeaderForSameSiteCookie = () => {
    HomeClient.installSameSiteHeader();
    addCtiLogEntry('Set Home Client SSNC header.');
};

export const setOverrideTvxClientType = (type) => {
    HomeClient.overrideTvxClientType(type);
    addCtiLogEntry(`Set HomeClient X-TVXClientType header to ${type}.`);
};

// The app can mount twice per page and each mount attaches. Keep the handler so
// the previous listener can be removed, or host messages get handled once per
// listener.
let hostMessageHandler = null;

export const attachIntegrationHostSystemListener =
    () => (dispatch, getState) => {
        setHomeClientHeaderForSameSiteCookie();
        setOverrideTvxClientType(CTI_INTEGRATION_CLIENT_TYPE_HEADER);

        fetchFeatureFlagsForUser(dispatch)
            .catch((error) => {
                addCtiLogEntry(
                    `fetchFeatureFlagsForUser GET ${error.responseURL} status:${error.statusText} response code:${error.status}`,
                );
            })
            .finally(() => {
                if (hostMessageHandler) {
                    window.removeEventListener(
                        'message',
                        hostMessageHandler,
                        false,
                    );
                }

                hostMessageHandler = handleEventFromHostSystem(
                    dispatch,
                    getState,
                );
                window.addEventListener('message', hostMessageHandler, false);
                addCtiLogEntry('Attached host system listener.');
            });
    };
